import { ISectionHelp } from './interface/helpkit.interface';

/**
 * RICH TEXT REFERENCE — how to write description strings
 * -------------------------------------------------------
 *
 * FORMATTING
 *   **bold**           → bold text
 *   *italic*           → italic text
 *   ### Heading        → section heading
 *   ---                → horizontal divider
 *   →                  → arrow (use literal → character)
 *
 * LINE BREAKS
 *   \n                 → single line break (new line, same block)
 *   \n\n               → new paragraph (visible gap between blocks)
 *
 * LISTS
 *   - item             → bullet point
 *   1. item            → numbered list item
 *   💡 tip text        → tip/hint line (use after \n)
 *
 * HASHTAGS
 *   #tag               → hashtag displayed with secondary theme color
 *
 * PATTERNS
 *
 *   Bold label + description:
 *     '**Label**\n' +
 *     'Description text.\n\n' +
 *     '**Next Label**\n' +
 *     'Next description.\n\n'
 *
 *   Bullet list:
 *     'Intro text:\n' +
 *     '- First item\n' +
 *     '- Second item\n\n'
 *
 *   Numbered steps:
 *     'Steps:\n' +
 *     '1. First step\n' +
 *     '2. Second step\n\n'
 *
 *   Tip line:
 *     'Description text.\n' +
 *     '💡 Tip on its own line.\n\n'
 *
 * RULES
 *   - Always end a section with \n\n before the next **Label**
 *   - Single \n = line break within the same block
 *   - \n\n = blank line / paragraph gap
 */

export const SECTION_HELP: ISectionHelp = {
  //Manager Conference Dashboard
  dashboard: {
    header: {
      icon: 'dashboard',
      title: 'Dashboard',
      description:
        'The Dashboard is your at-a-glance home for the project. Here you get a quick overview of engagement and activity without having to dig into the individual sections.\n\n' +
        'The page shows overview stats, as verified participants, chat messages, questions asked, private messages, discussions, video meetings, e-cards exchanged, and participant cities.\n\n' +
        "You'll also find a **to-do list** where you can add, edit, complete, and delete tasks. Useful for keeping track of what needs to be done before, during, and after the event.\n\n" +
        'The Dashboard is a status overview only. All editing and configuration is done in the other sections of the admin.',
      bullets: [],
    },
  },

  //Project SetUp
  generalSettings: {
    header: {
      icon: 'settings',
      title: 'General Settings',
      description:
        'The control center for this project. Publish the project, set language, URL and domain, choose how participants register and log in, and edit your branding. You can also connect Pretix, manage email white-labelling, add managers and adjust notifications. Click Save to apply changes.\n' +
        'These settings are usually not changed often.',
      bullets: [],
    },
    basicInfo: {
      icon: 'settings',
      title: 'Basic Info',
      description:
        '**Published / Not Published**\n' +
        'This is where you control whether the platform is live or not. When published, the locked pages become accessible to verified participants. When not published, all participants see the Welcome page instead. Moderators and speakers can still access the locked pages regardless.\n\n' +
        'To edit what participants see when the platform is not yet published, go to **Pages → Welcome**.\n\n' +
        '**Language**\n' +
        'Set the platform language to Swedish or English.\n\n' +
        '**URL name**\n' +
        'Edit the URL name directly in Basic Info. If you want to use a custom domain, contact Coeo Support.',
      bullets: [],
    },
    registrationType: {
      icon: 'how_to_reg',
      title: 'Registration type',
      description:
        '**What does Registration type control?**\n' +
        'This setting sets how:\n' +
        '\\- **Participants** can login for the event\n' +
        '\\- What status **participants** receive upon registration.\n\n' +
        '**Anonymous access**\n' +
        '\\- Participants can join using only a **display name**.\n' +
        '\\- No email address or account is created.\n' +
        "💡 Best for open information events or demos where you don't want a login requirement at all.\n\n" +
        '**Open registration**\n' +
        '\\- **Anyone** with the event link can register immediately, with no verification step required.\n' +
        '💡 Best for open events such as open webinars.\n\n' +
        '**Open registration, verify email**\n' +
        '\\- **Anyone** with the event link can register.\n' +
        '\\- The participant receives a verification email and **must click** the link/apply the code before they can log in.\n' +
        '💡 Best for open events where you still want to confirm that the email address is valid.\n\n' +
        '**Invitation only**\n' +
        '\\- Only **invited** participants can register.\n' +
        '\\- Each participant receives a **personal invitation link** via email to access the registration form. Send them their unique invitation link via **Participants & Communication → Send Emails → Invite Participants**.\n' +
        '💡 Best for closed events or premium events where you want full control over who attends.\n\n' +
        '**Use Login Links Instead of Password**\n' +
        'An additional toggle available regardless of registration type. When enabled, participants receive a login link that takes them directly into the platform, no password needed. Can also be used alongside password-based login, where login links still work as an alternative.\n' +
        '💡 Best for Open registration – anyone can register without needing a password.',
      bullets: [],
    },
    designBrand: {
      icon: 'palette',
      title: 'Design & Brand',
      description:
        'This is where you customize the look and feel of your platform.\n\n' +
        '**Title**\n' +
        'The event name shown in the browser tab, emails, and Google search preview.\n\n' +
        '**Meta Description**\n' +
        'A short description of the event used for Google search results.\n\n' +
        '**Image**\n' +
        'Upload an image (JPEG, PNG, or WebP, 1200 × 630 px). Used when the platform link is shared on social media or in emails.\n\n' +
        '**Favicon**\n' +
        'Upload a PNG (180 × 180 px) that appears as the icon in the browser tab and as the icon if using the mobile-app.\n\n' +
        '**Colors**\n' +
        'Set the three core colors for the platform:\n' +
        '- **Background** – the main background color\n' +
        '- **Primary** – used for buttons and key interactive elements\n' +
        '- **Secondary** – used as a complementary accent color\n\n' +
        '**Fonts**\n' +
        'Choose fonts for body text and headings separately. Fonts are sourced from Google Fonts. Browse available options at fonts.google.com. Type the exact Google Font name into the field to apply it.',
      bullets: [],
    },
    pretix: {
      icon: 'ticket',
      title: 'Ticketing (Pretix)',
      description:
        'Connect this project to Pretix to **manage** and/or **sell** tickets. Enter the Pretix slug for the event. Once saved, you get quick links to Pretix Admin and the public Ticket Shop. No slug field showing? No Pretix organisation is connected yet.\n\n' +
        '💡 What is a slug? \n\n' +
        '**Example:**\n' +
        'If the url is pretix.eu/control/event/dinorg/**varkonferens-2026/**\n' +
        '→ This is the slug: **varkonferens-2026**',
      bullets: [],
    },
    manage: {
      icon: 'manage_accounts',
      title: 'Manage',
      description:
        '**Email Settings (White Label)**\n' +
        'E-mail address and sender name that should be used when e-mails are sent to participants. A verification message will be sent to the e-mail, if the domain not already setup.\n\n' +
        '**Managers**\n' +
        "Manage who has access to the platform's admin. Visible to super-admins only.\n\n" +
        '**Notification Settings**\n' +
        'Turn email notifications for the platform on or off. Changes are saved instantly, no save button needed.',
      bullets: [],
    },
    advanced: {
      icon: 'code',
      title: 'Advanced',
      description:
        'An advanced section intended primarily for technical configuration. Contains JSON editors for language settings, general conference settings, custom registration fields, and GDPR/privacy policy. Contact Coeo Support if you need help with these settings.',
      bullets: [],
    },
  },
  integrations: {
    header: {
      icon: 'integration_instructions',
      title: 'Integrations',
      description:
        'Connect Coeo with your existing systems via APIs and webhooks — such as CRM systems, HR and recruitment platforms, ERP and business systems, accounting software, customer service platforms, and other business-critical applications. Get in touch with the Coeo team to learn more.',
      bullets: [],
    },
  },
  //Content
  pages: {
    header: {
      icon: 'web',
      title: 'Pages',
      description:
        'Pages is the page builder in Coeo. This is where you create and design all the pages that make up your event platform.\n\n' +
        '### Default Pages\n\n' +
        'Your platform comes with a set of pre-built pages, each with a specific purpose:\n\n' +
        '**Home** The public landing page, accessible to everyone **without login**. Use it to present your event and guide visitors to register or log in.\n\n' +
        '**Register** Where participants create their profile. You can customize which fields are shown depending on how simple or detailed you want the registration process to be.\n\n' +
        '**Login** The login page for participants with an existing profile.\n\n' +
        '**Reception** The first page participants see **after logging in**. Think of it as the lobby of your event. From here, navigation to other parts of the platform should be clear and easy.\n\n' +
        '**Event** Used to highlight a specific event or activity, with an image, program, and "Add to calendar" function.\n\n' +
        '**Welcome** Shown to participants who have created a profile when the logged-in platform is not yet published. Useful if you want to open up for registrations before letting participants in. The text is fully customizable.\n\n' +
        '### Adding New Pages\n\n' +
        'Beyond the default pages, you can create your own, for example for speakers, schedule details, or exhibitors.\n\n' +
        '- Click **Add page** to start with a blank page\n' +
        '- Click the **copy icon** on any page card to duplicate an existing page as a starting point\n\n' +
        '### Editing a Page\n\n' +
        'Click any page card to open the **Page Editor**. From here you can add and rearrange content blocks, preview the page in desktop/tablet/mobile, and adjust page settings.',
      bullets: [],
    },
    pageEditor: {
      icon: 'edit',
      title: 'Page Editor',
      description:
        'The Page Editor is where you design and build the content of each page using **components** — the building blocks that make up your page structure.\n\n' +
        'The editor has three main areas:\n\n' +
        '- **Left rail** – your component tree, where you add and organize content\n' +
        '- **Center** – a live preview as participants will see it\n' +
        '- **Top toolbar** – switch between desktop/tablet/mobile preview, undo, access Page Settings, and publish',
      bullets: [],
    },
    layout: {
      icon: 'view_quilt',
      title: 'Layout',
      description:
        '**Section** Always start building your page by choosing Section. Then place your other content inside the section. Section controls the width of your content area. Choose between Full Width, Small, Medium, Large, or X-Large depending on how much margin you want on the sides. You can also set the vertical spacing above and below the section.\n\n' +
        '**Row** Use Row to place content side by side horizontally. You can add up to four columns. Add a Content Area inside each column to fill it with content.\n\n' +
        '**Content Area** Used in two ways:\n- As a column inside a Row, to fill it with content and control its width\n- As a container with a background image, e.g. for a full-width header. Set the column width in fourths under the width settings.\n\n' +
        '**Top Menu** Adds a navigation bar at the top of the page. Choose the width in the dropdown, or select Max Width to stretch it edge to edge. Manage the menu items via **Pages → Menu Settings**.',
      bullets: [],
    },
    content: {
      icon: 'text_fields',
      title: 'Content',
      description:
        '**Styled Text** Add text to your page. You can adjust text size, apply bold or italic formatting, insert links, and create tables.\n\n' +
        '**Image** Add an image to your page. You can also link the image so participants are taken to a specific page or URL when they click it.\n\n' +
        '**Button** Add navigation buttons to help participants find their way around the platform. Set the button size by choosing how many buttons appear side by side. You can also customize icon, text, and color. Link to an external URL or use Pick Local Link to link to a page within the platform.\n\n' +
        '**Video** Embed an MP4 video on your page. The video must first be uploaded via **Content → Media Library** before it can be added here.',
      bullets: [],
    },
    setupAndTools: {
      icon: 'build',
      title: 'Setup and Tools',
      description:
        '**Program** Displays the program view on the page. Manage and update the program via **Content → Program** in the left menu.\n\n' +
        '**Rooms** Displays direct-access buttons to the active live streams for the current event day. The correct tracks are shown automatically based on which day is set to **In Reception**. You control this in **Content → Program**. Remember to update this manually as the event progresses.\n\n' +
        '**Add to Calendar** Adds a button that lets participants save the event to their private calendar.\n\n' +
        '**Calendar List** Displays a list of upcoming, ongoing, or past activities and events on the page. The status of each activity is managed via **Content → Program** in the left menu.\n\n' +
        '**Collection List** Displays a collection from your Collections feature. Choose how many rooms appear side by side, this also controls their size. Manage your collections via **Content → Collections**.\n\n' +
        '**Add Web App** Adds a button that lets participants save the platform as an app icon on their mobile home screen. An instruction guide is shown for the participants when the button is clicked.\n\n' +
        '**Login/Register** Adds a button that takes participants to the login or create profile page.\n\n' +
        '**File Manager** A document tool where you can upload files for participants to download. Choose whether all participants or only moderators can upload files. Useful for speaker presentations or shared documents on member platforms.\n\n' +
        '**Blog Component** Add a blog or news feed to your page. Write a title, set a date, and add images. Each post appears in a scrollable feed.\n\n' +
        '**Pretix Shop** Embeds your Pretix registration form directly on the page. Requires that you are using Pretix as your registration system.\n\n' +
        '**Pretix QR Code** Displays a QR code for on-site check-in using Pretix Scan. Requires that participants have been imported from Pretix.\n\n' +
        '**Custom Fields** Collect additional information from participants, e.g. a mailing address for sending gifts or goodie bags. Fields are configured via **Website and Info** under **Advanced Settings**. Contact Coeo Support for help with setup.\n\n' +
        '**Free HTML** Paste an embed code to display external content on the page, such as a Google Maps map or a Google Forms survey.',
      bullets: [],
    },
    interactivity: {
      icon: 'bolt',
      title: 'Interactivity',
      description:
        '**Poll** Add a poll with multiple answer options to engage your participants and collect quick feedback. Manage your polls and settings via **Interactivity & Engagement → Polls** in the left menu.\n\n' +
        '**User Goals** Motivate participants to engage with the platform by completing tasks to earn points, such as chatting with others, visiting exhibitors, or asking a speaker a question. Tasks are partly customizable.\n\n' +
        '**Forum List** Displays the latest forum activity as a list on the page. Useful for keeping participants up to date with ongoing discussions.\n\n' +
        '**Forum Posts** Lets participants create new discussion posts directly from the page. Posts appear in the forum where others can reply and join the conversation.\n\n' +
        '**Post Reply** Highlights a specific forum question on the page. Great for sparking engagement during an event. Example: "Which speaker did you enjoy the most today?" Place it on the Reception page to make it visible to all participants as they log in.\n\n' +
        '**Post Image** Lets participants upload or take a photo via their webcam. Uploaded images are displayed as a slideshow. *Contact Coeo Support for help with setup.*',
      bullets: [],
    },
  },
  program: {
    header: {
      icon: 'calendar_month',
      title: 'Program',
      description:
        '### Status Function\n\n' +
        'The status function helps you organize and control which event days are displayed on the event page. This feature is especially useful for recurring events hosted on the same platform.\n\n' +
        'You can choose between four different statuses:\n\n' +
        '- Active\n' +
        '- Planned\n' +
        '- Archived\n' +
        '- Hidden\n\n' +
        'How to change status\n\n' +
        '1. Click the three dots next to the event day name or date and choose "Edit day"\n' +
        '2. Select the preferred status.\n\n' +
        '💡 **Tip:**\n' +
        'Use the calendar feature (activated via *Pages*) to give participants an overview of upcoming and previous events and activities.\n\n' +
        '---\n\n' +
        '**Active**\n\n' +
        'Active is used for event days that should be visible in the program on the event page.\n\n' +
        '**In Reception**\n\n' +
        'The *In Reception* setting controls which day is displayed in the left-side menu on the event page.\n\n' +
        'When a new day should be displayed, manually update which day is marked as *In Reception* by selecting **Show in Reception**.\n\n' +
        '---\n\n' +
        '**Planned**\n\n' +
        'Planned is used for upcoming event days that should not yet appear as active.\n\n' +
        'If you use the calendar list on the event page, participants can see which activities are scheduled ahead.\n\n' +
        '---\n\n' +
        '**Archived**\n\n' +
        'Archived is used for completed event days.\n\n' +
        'Archived days are no longer displayed as active in the program.\n\n' +
        '---\n\n' +
        '**Hidden**\n\n' +
        'Hidden is used to hide event days from the event page without deleting them.',
      bullets: [],
    },
    programDayStatus: {
      icon: 'visibility',
      title: 'Day status & In Reception',
      description:
        '**What does Status control?**\n' +
        'This setting controls which event days are **visible** on the event page.\n' +
        'Especially useful for recurring events hosted on the same platform.\n\n' +
        '**Active**\n' +
        'The event day is **visible** in the program on the event page.\n' +
        '💡Use this for days that are currently open and available to participants.\n\n' +
        '**Planned**\n' +
        "The event day is **upcoming** but not yet active. It won't appear as active in the program.\n" +
        '💡If you use the calendar on the event page, participants can still see planned days ahead of time.\n\n' +
        '**Archived**\n' +
        'The event day is **completed** and no longer displayed as active in the program.\n' +
        '💡Use this to keep a clean program view after an event day has passed.\n\n' +
        '**Hidden**\n' +
        'The event day is **hidden** from the event page without being deleted.\n' +
        '💡Use this when you need to temporarily remove a day without losing its content.\n\n' +
        '---\n\n' +
        '**What does In Reception mean?**\n' +
        'This setting controls which day is **displayed in the left-side menu** when a participant enters the event page. Only **one day** can be In Reception at a time.\n\n' +
        'To update it: click the three dots next to the event day and select **Show in Reception**.',
      bullets: [],
    },
  },

  broadcast: {
    header: {
      icon: 'live_tv',
      title: 'Broadcast',
      description:
        'Broadcast. Here you can see at a glance how your broadcast is performing, what needs your attention, and what your team has been doing lately.',
      bullets: [],
    },
  },

  programRoomVideoAssets: {
    header: {
      icon: 'ondemand_video',
      title: 'Program room video assets',
      description: 'Browse and manage uploaded video assets from Mux',
      bullets: [],
    },
  },
  collections: {
    header: {
      icon: 'collections',
      title: 'Collections',
      description:
        '**Collections** is where you build the structured content participants browse during the event. Sponsors, exhibitors, breakout rooms, poster sessions and similar items. You organize everything two ways that work in parallel:\n\n' +
        '**Lists** are ordered groups of items shown in the live conference. A list controls what participants actually see and in what order so you decide both the grouping and the sequence.\n\n' +
        '**Categories** are labels that classify your items (categories are not mandatory). Example: "Gold Sponsor", "Workshop" or "Research Poster".\n\n' +
        'Build your categories and lists here, then add items to populate them.\n\n' +
        '### Examples of what you can build:\n\n' +
        '**Content:**\n' +
        '- **Sponsors & exhibitors:** A list of all sponsors, categorized by tier (Gold, Silver, Bronze). Each sponsor gets a profile page with logo, description, links and contact details. Participants can filter by tier and browse between exhibitors.\n\n' +
        '- **Poster exhibition:** Research posters or project presentations grouped in a list, categorized by **topic** (e.g. "Clinical Research", "Education", "Policy"). Each poster becomes a page with an image or PDF, abstract and authors.\n\n' +
        '- **Speakers:** A speaker directory where each speaker has a profile with photo, bio and the sessions they appear in. Can be categorized by track or day.\n\n' +
        '**Interactivity:**\n' +
        "- **Breakout rooms:** A breakout list of live video rooms participants drop into. Moderators can manage who's where by manually assigning, searching, or shuffling participants across rooms.\n\n" +
        "- **Bookable meetings & sessions:** A list with time slots participants book in advance, for speed networking, workshops, demos or one-on-one meetings. Moderators get a booking overview to review and adjust who's booked into each slot.\n\n" +
        '- **Digital collections / booths:** Virtual exhibitor booths in an collection view, categorized by industry or product type.\n\n' +
        '### Tagging items\n\n' +
        'Each item can carry #hashtags, added directly in its title or short description. Hashtags let participants filter and sort the items within a list. Think of them more as **themes** than strict categories.\n\n' +
        '**Example:** #blue, #yellow, #green, #red. Use an underscore for spaces (#Main_Stage shows as "Main Stage"), and add a hex code after a dash to give the tag a matching color (#blue-3B5998). This is a quick, free-form way to theme and group items on the fly – lighter and looser than the formal Categories.',
      bullets: [],
    },
  },
  mediaLibrary: {
    header: {
      icon: 'photo_library',
      title: 'Media Library',
      description:
        '**Media Library**\n\n' +
        "The Media Library is your project's central store for everything you upload: images, videos, files and stream recordings. You upload media here once and reuse it across the project. Images (JPEG, PNG, WebP) give you click-to-copy URLs in several sizes, videos are encoded automatically and can be previewed when ready, files like PDFs and slides get public download links, and past live streams appear as recordings. You can bulk-delete items on any tab.\n\n" +
        '**The difference between the Media Library and Pages**\n\n' +
        'Pages is where you build the pages participants see, placing images and content where you want them in the layout. When you add an image inside a page, it uses the same backend as the Media Library, so it is the same underlying file store. The difference is purpose: in Pages you place and present, in the Media Library you manage and reuse. An image uploaded inside a page lands in the same place, so you never upload twice. To reuse an image across several pages, upload it once in the Media Library and copy its URL from there.',
      bullets: [],
    },
  },
  //Participants & Communications
  participants: {
    header: {
      icon: 'people',
      title: 'Participants',
      description:
        'Here is where you manage everything related to participants on the platform. From here you can import participants, view their status, edit and delete participants.\n\n' +
        '### Status\n' +
        "At the top of the page you'll find four status levels:\n\n" +
        '**Verified**\n' +
        'The participant has created a profile and has access to the logged-in/locked pages on the platform.\n\n' +
        '**Imported**\n' +
        'The participant has been imported but has not yet created a profile or login.\n\n' +
        '**Email not verified**\n' +
        'Shown when the registration type is set to "Open, but verify email." The participant has filled in the registration form but has not yet verified their email address.\n\n' +
        '**Banned**\n' +
        'As a manager you can ban any participant from accessing the platform. Banned participants can no longer log in to the locked pages.\n\n' +
        '### New Participant\n' +
        'Add a single participant manually without importing a list.\n\n' +
        '### Import Participants\n' +
        'Import participant lists in two ways:\n\n' +
        '\\- **Upload CSV file**: select a CSV file and preview the list before importing. Duplicates are removed automatically when imported. Choose which role to import participants as: Participant, Speaker, Moderator, or Moderator and Speaker.\n' +
        '\\- **Text field**: paste a list directly, e.g. useful when you only have email addresses and want to import quickly.\n\n' +
        '### Columns\n' +
        'Choose which columns are visible in the participant list by toggling them on or off.\n\n' +
        '### Export\n' +
        'Export the full participant list.\n\n' +
        '### How to invite participants\n\n' +
        '1. Import participants. Email address is required.\n' +
        '2. Go to **Participants & Communication → Send Emails** and select **Invite Participants**.\n' +
        '3. Compose the email using the available fields in the email template.\n' +
        '4. Proceed to the next step and select who to invite. To invite all imported participants, filter by imported or click **All Imported Not Invited**, then select all using the checkbox in the top left.',
      bullets: [],
    },
  },
  tags: {
    header: {
      icon: 'label',
      title: 'Tags',
      description:
        'Let participants select tags when creating their profile to give a better overview and help them find matching interests among other participants.\n\n Add the tags you want participants to choose from, such as job title, location, interests, skills, or networking goals. There are two tag levels, each with its own color, allowing you to create distinct categories e.g. one for "role" and one for "what you are looking for." Participants can then filter by tags in the participant list to find the right match.',
      bullets: [],
    },
  },
  listsOfParticipants: {
    header: {
      icon: 'list',
      title: 'Lists of participants',
      description:
        'Lists is used to sort and categorize participants into different groups. This makes it easier to tailor communications to specific roles, for example sending one type of email to exhibitors and another to speakers.',
      bullets: [],
    },
  },
  sendEmails: {
    header: {
      icon: 'email',
      title: 'Send Emails',
      description:
        'Send Emails is where you invite participants to the platform or send information to those who are already registered.\n\n' +
        '**How to invite participants**\n\n' +
        '1. Import participants in **Participants & Communication → Participants**. Email address is required.\n' +
        '2. Go to **Participants & Communication → Send Emails** and select **Invite Participants**.\n' +
        '3. Compose the email using the available fields in the email template.\n' +
        '4. Proceed to the next step and select who to invite. To invite all imported participants, filter by imported or click **All Imported Not Invited**, then select all using the checkbox in the top left.',
      bullets: [],
    },
    sendEmailNow: {
      icon: 'send',
      title: 'Send Email Now',
      description:
        '**Invite Participants:** Sent to participants with the status "Imported" — those who have been added to the platform but have not yet created a profile.\n\n' +
        '**Send Information:** Used for reminders and general information. Sent only to participants who have created a profile.\n\n' +
        '**Send Login Link:** Sends a direct login link that automatically logs the participant into the platform, no password needed. Can only be sent to verified participants, i.e. participants who have already created a profile or been imported as verified.\n\n' +
        '💡 Participants are imported as verified automatically when "Use login link instead of password" is enabled under Registration Type in General Settings. This is also the recommended setup if you want to use login links as your primary way of letting participants in.',
      bullets: [],
    },
    automatedEmailTemplates: {
      icon: 'mark_email_read',
      title: 'Automated Emails',
      description:
        'Automated emails are sent to participants automatically at specific moments. Both can be freely edited and customized.\n\n' +
        '**Welcome/Confirmation:** Sent automatically when someone creates a profile on the platform.\n\n' +
        '**Welcome/Confirmation Login Links:** Used when you have enabled "Open registration" and checked "Use login link instead of password" in General Settings. Sent automatically when a participant registers with their name and email.',
      bullets: [],
    },
  },
  //Analytics & Reports
  analyticsReports: {
    header: {
      icon: 'analytics',
      title: 'Analytics Reports',
      description:
        'Analytics & Reports gives you an overview of engagement and activity on your platform.',
      bullets: [],
    },
    insights: {
      icon: 'insights',
      title: 'Insights',
      description:
        'A quick overview of activity on the platform:\n\n' +
        '- **Verified participants** – total number of participants who have created a profile\n' +
        '- **Room Chat** – number of chat messages sent in program rooms\n' +
        '- **Room ASK** – number of questions asked in program rooms\n' +
        '- **Private messages / Conversations** – total private messages and started conversations\n' +
        '- **Replies / Discussions** – activity in the forum\n' +
        '- **Video meetings** – number of video meetings started\n' +
        '- **E-Cards exchanged** – number of digital business cards exchanged\n' +
        '- **Participant Cities** – number of distinct cities participants registered from. Click **Show** to see the full list.',
      bullets: [],
    },
    participantGoals: {
      icon: 'flag',
      title: 'Participant Goals',
      description:
        'Shows the engagement scores connected to the **User Goals** component set up in Pages. Set goal weights using sliders (0–10) for different engagement activities, such as visiting the collections, posting in the forum, or sending private messages. Click **Recalculate list** to rank all participants by their score based on the current weights. The participant table can be exported as CSV or PDF.\n\n' +
        '💡 The weights set here should match the settings in the User Goals component on the platform for scores to be consistent.',
      bullets: [],
    },
    participantDailyLogins: {
      icon: 'login',
      title: 'Participant Daily Logins',
      description:
        'See how many participants logged in on each day within a selected date range. Download a CSV for any specific day containing name, email, title, company, and city for each participant who logged in that day.',
      bullets: [],
    },
    programRoomViewStats: {
      icon: 'bar_chart',
      title: 'Program Room View Stats',
      description:
        'Drill into each program room to see a timeline of views per day. Download a list of participants who viewed a specific room, including name, email, title, company, and city.',
      bullets: [],
    },
    moreStatistics: {
      icon: 'analytics',
      title: 'More Statistics',
      description:
        "You can embed a **Plausible** dashboard for privacy-friendly website traffic statistics, showing page views, visitor numbers, and traffic sources for your platform's public pages.",
      bullets: [],
    },
  },
  // Interactivity & Engagement
  mingleRooms: {
    header: {
      icon: 'video_chat',
      title: 'Mingle Rooms',
      description:
        'Mingle Rooms is the live video networking space – a digital mingle where participants can either choose which room to join or be randomly assigned to a room via Chat Roulette. Mingle Rooms are persistent and unscheduled, always available during the event. Use them for things like "Coffee Break", "VIP Networking", or "Speaker Q&A".\n\n' +
        'Here you create and manage the rooms. For each room you set a name, display order, and an optional background image. What happens live inside the rooms, who is currently in them, is only visible to participants on their side of the platform.',
      bullets: [],
    },
  },
  polls: {
    header: {
      icon: 'polls',
      title: 'Polls',
      description:
        'Create polls with multiple answer options to engage participants during live sessions. Polls are connected to program rooms and controlled live by the moderator during the session, but can also be added to any page via the Poll component in Pages.\n\n' +
        'During a live session, the moderator controls the poll experience via the **Polls tab** in the moderator view.',
      bullets: [],
    },
  },
  moreInteractivity: {
    header: {
      icon: 'emoji_objects',
      title: 'More Interactivity',
      description:
        "Looking for more ways to engage your participants? Interactive features are not managed from a menu of their own — you add them as components on a page in **Pages**. That's where you'll find components and where you place and arrange them on the project site.\n\n" +
        '### Interactive components examples:\n\n' +
        '- **Poll:** Run votes with multiple answer options.\n\n' +
        '- **User Goals:** Boost engagement by letting participants earn points for completing missions, such as chatting with a set number of people, visiting exhibitors or asking a speaker a question.\n\n' +
        '- **Forum List:** Show the latest forum activity as a list.\n\n' +
        '- **Forum Posts:** Let participants start new discussions that others can reply to, creating threads.\n\n' +
        '- **Post Reply:** Surface a single forum question directly on a page (for example on Reception), great for sparking interaction — like "Which speaker did you enjoy most today?"\n\n' +
        '- **Post Image:** Let participants upload or snap a webcam photo, which you can turn into a slideshow. Contact Coeo Support to set this up.\n\n' +
        '**Go to pages** to add these components to your platform.',
      bullets: [],
    },
  },
  //Invoice
  invoice: {
    header: {
      icon: 'receipt_long',
      title: 'Invoice',
      description:
        "The Invoice page connects your project's Pretix ticket orders to Fortnox accounting, so you can create invoices in bulk without leaving the platform. The page has two tabs: Fortnox Settings and Pretix Order.\n\n" +
        '### Fortnox Settings: Map your categories\n\n' +
        'This is where you tell Fortnox which account each type of ticket revenue belongs to.\n\n' +
        '- Each Pretix category (e.g. "Anmälan", "Logi") is shown on the left\n' +
        '- Map it to the right Fortnox account on the right (e.g. "2421 - Biljettförsäljning faktura")\n' +
        "- Click **Sync New Categories** to pull in any categories you've added in Pretix since last time\n\n" +
        '### Pretix Order: Create the invoices\n\n' +
        'This is where you fetch orders and turn them into Fortnox invoices.\n\n' +
        '- Click **Fetch Order from Pretix** to pull the latest orders into the list, each showing customer, order details, total, Pretix status and Fortnox status\n' +
        '- Filter the list by **Pretix status** or **Fortnox status** to find the orders you want\n' +
        "- Use **View more details** to inspect a single order's items and attendees\n" +
        '- Tick the checkbox on one or more orders to select them\n' +
        '- Click **Create Invoice** to generate Fortnox invoices for all selected orders at once — each line posted to the right account based on your category mapping\n\n' +
        "Once an invoice is created, the order's Invoice creation date is filled in and its Fortnox **status** updates. Payment status is then tracked in Fortnox itself, not here.",
      bullets: [],
    },
  },

  //Manager Dashboard (mylist )
  projects: {
    header: {
      icon: 'dashboard',
      title: 'Projects',
      description:
        'Your project overview with all platforms and events you manage are shown here as cards.\n' +
        "Click a card to open the project's admin workspace.\n\n" +
        'Each card has a few quick actions:\n' +
        '- **Heart icon** – mark a project as a favorite so it floats to the top of the list\n' +
        '- **Copy** – duplicate an existing project as a starting point for a new one\n' +
        '- **Visit** – open the public platform URL in a new tab\n' +
        '- **Pretix** – open the Pretix control panel (only visible when Pretix is configured)\n\n' +
        'To create a new project, click **New from template** and choose between starting blank or using an existing template.\n' +
        'You can also duplicate any existing project by clicking Copy on its card, all settings and content are then copied over so you can start customizing straight away.',
      bullets: [],
    },
  },
  organizationDashboard: {
    header: {
      icon: 'apartment',
      title: 'Organization',
      description:
        'An overview of all projects within your organisation, not just the ones you manage personally. Projects are displayed as cards, just like on your main project list.\n\n' +
        'From here you can:\n' +
        '- **Click a card** to open that project’s admin workspace\n' +
        '- **Copy** a project to duplicate it as a starting point for a new one\n' +
        '- **Visit** to open the public platform URL in a new tab\n' +
        '- **Pretix** to open the Pretix control panel (only visible when Pretix is configured)\n\n' +
        'Projects where you are not assigned as a manager are shown in a faded style and are read-only.\n\n' +
        '💡 The only way to create a new project from this view is by duplicating an existing one via Copy. To create a blank project or use a template, go back to "My list".',
      bullets: [],
    },
  },
  myTodos: {
    header: {
      icon: 'assignment',
      title: 'Your TODOs',
      description:
        'A read-only overview of all to-dos assigned to you across all your projects. Tasks are grouped by project. Click on a project title to go directly to that project’s dashboard.\n\n' +
        'Each task shows its content, when it was created, and who created it. To add, edit, or complete a to-do, go to the dashboard of the relevant project.',
      bullets: [],
    },
  },
  forumActivity: {
    header: {
      icon: 'forum',
      title: 'Forum activity',
      description:
        'A read-only overview of forum activity across all your projects. Each project is shown as a card displaying the total number of replies and discussions, along with when the latest activity occurred. Projects with the most activity are ranked at the top so the most active forums are easy to spot.\n\n' +
        'Click a project title to go directly to that project’s forum.\n',
      bullets: [],
    },
  },
  manageOrganisations: {
    header: {
      icon: 'apartment',
      title: 'Organizations',
      description:
        'An overview of all organizations in the system. Each organization is shown as a card displaying its name and the managers connected to it.\n\n' +
        'From here you can:\n' +
        "- **Edit an organization** – click the edit icon on the card to update the organization's details\n" +
        '- **Add a new manager** – click **New Manager** to create a new manager account scoped to that organization\n' +
        '- **Connect an existing manager** – link a manager who already exists in the system to the organization\n' +
        "- **Edit or remove a manager** – edit a manager's details or unlink them from the organization\n\n" +
        '**New Organization**\n' +
        'Creates a new organization. When creating, you can choose to also create a matching organizer in Pretix, link to an existing Pretix organizer, or create a Coeo-only organization without Pretix.\n\n' +
        '**Connect to Pretix**\n' +
        'Authenticates your account with Pretix via OAuth. Required before you can create or link organizations to Pretix. Click the button and follow the Pretix login flow. Once connected, Pretix features become available across the organization settings.\n\n' +
        '💡 This section is intended for super admins. Regular managers typically only see their own organization and projects.',
      bullets: [],
    },
  },
  thirdPartyIntegrations: {
    header: {
      icon: 'integration_instructions',
      title: 'Third-party integrations',
      description:
        'Organization-wide credentials for external systems. These settings apply across the manager area: Pretix OAuth for ticketing and organizer flows, LiveKit server URL and API keys for media egress, and Fortnox OAuth for invoicing and bookkeeping.',
      bullets: [
        'Pretix: register an OAuth application in Pretix and store the client ID, secret, and scopes used when managers connect their Pretix account',
        'LiveKit: set the server host and API key/secret used by server features such as egress',
        'Fortnox: configure the Fortnox OAuth app credentials used for accounting integrations',
      ],
    },
  },
  upcoming: {
    header: {
      icon: 'date_range',
      title: 'Upcoming',
      description:
        'See scheduled program days grouped by month and day. Jump to a project dashboard from each card.',
      bullets: [
        'Program days from today onward',
        'Refreshes automatically every ten minutes',
      ],
    },
  },
  liveStreams: {
    header: {
      icon: 'live_tv',
      title: 'Live streams',
      description: 'Browse live recordings and Mux video assets',
      bullets: [],
    },
  },
  videoAssets: {
    header: {
      icon: 'ondemand_video',
      title: 'Video assets',
      description: 'Browse and manage uploaded video assets from Mux',
      bullets: ['Browse and manage uploaded video assets from Mux'],
    },
  },
};
